import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabInsights.module.css';
import MetaHead from '../components/MetaHead';

/* ============================================================
   TabInsights — onglet "Insights IA"
   ------------------------------------------------------------
   IDs DOM préservés pour le backend Express :
     #ins-period-select   ← select "30j / 90j / 6 mois"
     #ins-themes-list     ← conteneur thématiques
     #ins-timeline        ← timeline historique
     #ins-trends-week     ← bloc tendances semaine
     #ins-refresh-btn     ← bouton rafraîchir

   Callbacks (props) :
     - onPeriodChange(period)
     - onRefresh()
     - onThemeClick(themeId)

   i18n P5b : useTranslation('tabs') + MetaHead.
   Les CONTENUS (themes, summaries, keywords, timeline) sont déjà
   multilingues côté backend (Claude Haiku prompt passe `lang`).
   Seuls les LABELS UI (titres, badges, tags) sont traduits ici.
   Si t.tag est servi depuis le backend, il restera dans la langue
   de l'IA — on conserve la prop telle quelle (pas de mapping forcé).
   ============================================================ */

// Le defaults THEMES/TIMELINE/WEEK_TRENDS restent FR (échantillons de démo,
// remplacés en runtime par les vraies données IA déjà traduites).

const THEMES = [
  {
    id: 'ambiance',
    emoji: '🕯️',
    label: 'Ambiance',
    score: 4.7,
    sentiment: 95,
    mentions: 142,
    trend: '+12%',
    direction: 'up',
    keywords: ['chaleureuse', 'cosy', 'romantique', 'feutrée', 'authentique'],
    summary: "L'ambiance provençale du restaurant est votre signature. Les clients la mentionnent dans 1 avis sur 3.",
    tag: 'Force',
    tagColor: 'green',
  },
  {
    id: 'service',
    emoji: '🍽️',
    label: 'Service',
    score: 4.5,
    sentiment: 88,
    mentions: 198,
    trend: '+5%',
    direction: 'up',
    keywords: ['attentif', 'souriant', 'rapide', 'personnalisé', 'pro'],
    summary: "Léa et Marco sont cités nommément 23 fois ce mois. Le service est votre 2e atout.",
    tag: 'Force',
    tagColor: 'green',
  },
  {
    id: 'cuisine',
    emoji: '👨‍🍳',
    label: 'Cuisine',
    score: 4.4,
    sentiment: 82,
    mentions: 215,
    trend: '+2%',
    direction: 'flat',
    keywords: ['savoureuse', 'fraîche', 'généreuse', 'authentique', 'maison'],
    summary: "La bouillabaisse et la pissaladière concentrent 45% des éloges. Le tiramisu reçoit 3 critiques sur la portion.",
    tag: 'Stable',
    tagColor: 'accent',
  },
  {
    id: 'rapport',
    emoji: '💰',
    label: 'Rapport qualité-prix',
    score: 4.0,
    sentiment: 68,
    mentions: 87,
    trend: '−4%',
    direction: 'down',
    keywords: ['correct', 'cher', 'justifié', 'raisonnable', 'élevé'],
    summary: "8 avis ce mois mentionnent les prix comme un point d'attention. À surveiller sur les desserts.",
    tag: 'À surveiller',
    tagColor: 'amber',
  },
  {
    id: 'cadre',
    emoji: '🌿',
    label: 'Cadre & terrasse',
    score: 4.6,
    sentiment: 92,
    mentions: 76,
    trend: '+18%',
    direction: 'up',
    keywords: ['terrasse', 'vue', 'lumineux', 'tranquille', 'ensoleillé'],
    summary: "Avec le retour du soleil, la terrasse devient un argument fort. +18% de mentions vs mois dernier.",
    tag: 'Tendance',
    tagColor: 'green',
  },
  {
    id: 'attente',
    emoji: '⏱️',
    label: 'Temps d\'attente',
    score: 3.9,
    sentiment: 62,
    mentions: 54,
    trend: '+8%',
    direction: 'down',
    keywords: ['long', 'attente', 'service lent', 'patience', 'samedi soir'],
    summary: "Concentré sur les samedis soirs. 6 avis cette semaine. Renfort équipe à envisager le week-end.",
    tag: 'Action requise',
    tagColor: 'red',
  },
];

const TIMELINE = [
  {
    month: 'Avr. 2026',
    highlight: 'Bouillabaisse plébiscitée',
    items: [
      { type: 'up',   text: '+18% de mentions de la terrasse — saison qui démarre' },
      { type: 'up',   text: 'Léa citée 23 fois pour son service personnalisé' },
      { type: 'down', text: '6 avis sur le temps d\'attente le samedi soir' },
    ],
  },
  {
    month: 'Mars 2026',
    highlight: 'Service en progression',
    items: [
      { type: 'up',   text: 'Note service +0,2★ vs février' },
      { type: 'up',   text: 'Tiramisu maison apparaît 12 fois en positif' },
      { type: 'flat', text: 'Cuisine reste stable à 4,4★' },
    ],
  },
  {
    month: 'Fév. 2026',
    highlight: 'Saint-Valentin réussie',
    items: [
      { type: 'up',   text: '34 avis sur la soirée du 14 — note moyenne 4,8★' },
      { type: 'up',   text: 'Ambiance "romantique" mentionnée 28 fois' },
      { type: 'down', text: '4 avis sur le bruit en salle pleine' },
    ],
  },
  {
    month: 'Jan. 2026',
    highlight: 'Reprise post-fêtes',
    items: [
      { type: 'flat', text: 'Volume d\'avis stable (47 avis)' },
      { type: 'up',   text: 'Premier mois avec Revyo : +24 SMS envoyés' },
    ],
  },
];

const WEEK_TRENDS = [
  { label: 'Mots les + cités cette semaine', items: ['terrasse', 'soleil', 'bouillabaisse', 'Léa', 'pissaladière'] },
  { label: 'Émotions dominantes', emojis: ['😍 42%', '🤩 28%', '🙂 18%', '😐 8%', '😞 4%'] },
];

export default function TabInsights({
  period = '30d',
  themes = THEMES,
  timeline = TIMELINE,
  weekTrends = WEEK_TRENDS,
  onPeriodChange = () => {},
  onRefresh = () => {},
  onThemeClick = () => {},
}) {
  const { t } = useTranslation('tabs');
  const [selectedPeriod, setSelectedPeriod] = useState(period);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTheme, setActiveTheme] = useState(null);

  const PERIODS = [
    { value: '30d',  label: t('insights.period_30d') },
    { value: '90d',  label: t('insights.period_90d') },
    { value: '6m',   label: t('insights.period_6m') },
    { value: '1y',   label: t('insights.period_1y') },
  ];

  const handlePeriod = (e) => {
    setSelectedPeriod(e.target.value);
    onPeriodChange(e.target.value);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh(); } finally { setTimeout(() => setRefreshing(false), 800); }
  };

  // Sprint 2 P1 : mentions/sentiment peuvent être null (backend incertain).
  // On ignore les null dans les agrégats hero.
  const mentionsValues = themes.map(t => t.mentions).filter(m => typeof m === 'number' && Number.isFinite(m));
  const sentimentValues = themes.map(t => t.sentiment).filter(s => typeof s === 'number' && Number.isFinite(s));
  const totalMentions = mentionsValues.reduce((sum, m) => sum + m, 0);
  const avgSentiment = sentimentValues.length > 0
    ? Math.round(sentimentValues.reduce((sum, s) => sum + s, 0) / sentimentValues.length)
    : null;

  return (
    <div className={styles.wrap}>
      <MetaHead page="insights" />
      {/* HEADER */}
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1>
            <span className={styles.titleEmoji}>{t('insights.title_emoji')}</span>
            {t('insights.title')}
          </h1>
          <p>{t('insights.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <select
            id="ins-period-select"
            className={styles.periodSelect}
            value={selectedPeriod}
            onChange={handlePeriod}
          >
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button
            type="button"
            id="ins-refresh-btn"
            className={styles.btnGhost}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? t('insights.btn_refreshing') : t('insights.btn_refresh')}
          </button>
        </div>
      </header>

      {/* HERO STATS */}
      <section className={styles.heroStats}>
        <div className={styles.heroCard}>
          <div className={styles.heroLabel}>{t('insights.hero_sentiment_label')}</div>
          {avgSentiment !== null ? (
            <>
              <div className={styles.heroValue}>
                <span className={styles.heroBig}>{avgSentiment}</span>
                <span className={styles.heroPct}>%</span>
              </div>
              <div className={styles.heroBar}>
                <div className={styles.heroBarFill} style={{ width: `${avgSentiment}%` }} />
              </div>
              <div className={styles.heroSub}>{t('insights.hero_sentiment_sub')}</div>
            </>
          ) : (
            <>
              <div className={styles.heroValue}>
                <span className={styles.heroBigMuted}>—</span>
              </div>
              <div className={styles.heroSub}>{t('insights.hero_data_insufficient')}</div>
            </>
          )}
        </div>

        <div className={styles.heroCard}>
          <div className={styles.heroLabel}>{t('insights.hero_mentions_label')}</div>
          <div className={styles.heroValue}>
            <span className={styles.heroBig}>{mentionsValues.length > 0 ? totalMentions : '—'}</span>
          </div>
          <div className={styles.heroSub}>
            {mentionsValues.length > 0
              ? t('insights.hero_mentions_sub_full', { count: themes.length })
              : t('insights.hero_mentions_sub_partial', { count: themes.length })}
          </div>
        </div>

      </section>

      {/* THÉMATIQUES */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t('insights.section_themes_title')}</h2>
          <span className={styles.sectionSub}>{t('insights.section_themes_sub')}</span>
        </div>
        <div id="ins-themes-list" className={styles.themesGrid}>
          {themes.map((th) => (
            <article
              key={th.id}
              className={`${styles.themeCard} ${activeTheme === th.id ? styles.themeCardActive : ''}`}
              onClick={() => { setActiveTheme(th.id); onThemeClick(th.id); }}
              role="button"
              tabIndex={0}
            >
              <header className={styles.themeHead}>
                <div className={styles.themeEmoji}>{th.emoji}</div>
                <div className={styles.themeTitle}>
                  <div className={styles.themeName}>{th.label}</div>
                  {/* Sprint 2 P1 : si mentions null → on masque le compteur (pas de fausse valeur) */}
                  <div className={styles.themeMentions}>
                    {(typeof th.mentions === 'number' && Number.isFinite(th.mentions))
                      ? t('insights.mentions_count', { count: th.mentions })
                      : t('insights.mentions_unknown')}
                  </div>
                </div>
                <span className={`${styles.themeTag} ${styles[`themeTag_${th.tagColor}`]}`}>
                  {th.tag}
                </span>
              </header>

              <div className={styles.themeScoreRow}>
                <div className={styles.themeScore}>
                  <span className={styles.themeScoreValue}>{th.score.toFixed(1)}</span>
                  <span className={styles.themeScoreMax}>/5</span>
                </div>
                <div className={`${styles.themeTrend} ${styles[`themeTrend_${th.direction}`]}`}>
                  {th.direction === 'up' && '↗'} {th.direction === 'down' && '↘'} {th.direction === 'flat' && '→'} {th.trend}
                </div>
              </div>

              {/* Sprint 2 P1 : barre sentiment vide + label "Données insuffisantes" si null */}
              {(typeof th.sentiment === 'number' && Number.isFinite(th.sentiment)) ? (
                <div className={styles.themeBar}>
                  <div
                    className={`${styles.themeFill} ${styles[`themeFill_${th.tagColor}`]}`}
                    style={{ width: `${th.sentiment}%` }}
                  />
                </div>
              ) : (
                <div
                  className={styles.themeBarEmpty}
                  title={t('insights.data_insufficient_tooltip')}
                >
                  {t('insights.data_insufficient_short')}
                </div>
              )}

              <p className={styles.themeSummary}>{th.summary}</p>

              <div className={styles.themeKeywords}>
                {th.keywords.slice(0, 4).map((k) => (
                  <span key={k} className={styles.themeKw}>{k}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* TIMELINE — affichée seulement si données réelles fournies en prop */}
      {Array.isArray(timeline) && timeline.length > 0 && (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>{t('insights.section_history_title')}</h2>
          <span className={styles.sectionSub}>{t('insights.section_history_sub')}</span>
        </div>
        <div id="ins-timeline" className={styles.timeline}>
          {timeline.map((entry, i) => (
            <div key={entry.month} className={styles.timelineEntry}>
              <div className={styles.timelineDot}>
                <span>{i === 0 ? '✨' : '📍'}</span>
              </div>
              <div className={styles.timelineCard}>
                <header className={styles.timelineHead}>
                  <span className={styles.timelineMonth}>{entry.month}</span>
                  <span className={styles.timelineHighlight}>{entry.highlight}</span>
                </header>
                <ul className={styles.timelineList}>
                  {entry.items.map((it, j) => (
                    <li key={j} className={styles[`timelineItem_${it.type}`]}>
                      <span className={styles.timelineIcon}>
                        {it.type === 'up' && '▲'}
                        {it.type === 'down' && '▼'}
                        {it.type === 'flat' && '●'}
                      </span>
                      {it.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* TENDANCES SEMAINE — affichées seulement si données réelles fournies */}
      {Array.isArray(weekTrends) && weekTrends.length > 0 && (
      <section id="ins-trends-week" className={styles.weekTrends}>
        {weekTrends[0]?.items && (
        <div className={styles.weekCard}>
          <header className={styles.weekHead}>
            <h3>{weekTrends[0].label || t('insights.section_week_words_title')}</h3>
            <span className={styles.weekBadge}>{t('insights.week_badge')}</span>
          </header>
          <div className={styles.wordCloud}>
            {weekTrends[0].items.map((w, i) => (
              <span
                key={w}
                className={styles.cloudWord}
                style={{ fontSize: `${22 - i * 2}px`, opacity: 1 - i * 0.12 }}
              >
                {w}
              </span>
            ))}
          </div>
        </div>
        )}

        {weekTrends[1]?.emojis && (
        <div className={styles.weekCard}>
          <header className={styles.weekHead}>
            <h3>{t('insights.section_week_emotions_title')}</h3>
            <span className={styles.weekBadge}>{t('insights.week_badge')}</span>
          </header>
          <div className={styles.emotionsList}>
            {weekTrends[1].emojis.map((e, i) => (
              <div key={e} className={styles.emotionRow}>
                <span className={styles.emotionEmoji}>{e.split(' ')[0]}</span>
                <div className={styles.emotionBar}>
                  <div
                    className={styles.emotionFill}
                    style={{ width: e.split(' ')[1], animationDelay: `${i * 0.1}s` }}
                  />
                </div>
                <span className={styles.emotionPct}>{e.split(' ')[1]}</span>
              </div>
            ))}
          </div>
        </div>
        )}
      </section>
      )}
    </div>
  );
}
